import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store';
import { setStatus } from '../../store/userGenerationSlice';
import { generateInfraUsersLocal, validateUsersForDuplicates } from '../../services/api';
import translations from '../../locales/translations';
import { GenerateUser } from '../../types';
import type { StatusColor } from './UserGenerationTypes';
import { useNewGui } from '../../hooks/useNewGui';
import { FiUser, FiKey, FiServer, FiCalendar } from 'react-icons/fi';

interface Props {
  selectedLicenseServerId: string;
  infraServers: any[];
  startPolling: (operationId: string) => void;
  resetOperation: () => void;
  updateStatus: (msg: string, color: StatusColor) => void;
}

const defaultSingleUser = (): GenerateUser => {
  const today = new Date();
  const threeMonths = new Date();
  threeMonths.setMonth(threeMonths.getMonth() + 3);
  const format = (d: Date) => d.toISOString().split('T')[0];
  const storedSystem = localStorage.getItem('userGen.system');
  return {
    sid: '', username: '', userid: '', usertype: '', details: '',
    primary_group: '', sec_groups: '', email: '', mobileno: '',
    createdOn: '', enabled: true, system: storedSystem || 'vnc', mode: '',
    initialPassword: 'vlsiexpert@12345', tools: '',
    setupDate: format(today), expiryDate: format(threeMonths), duration: '',
    automation: 'planned', puttyStart: '', puttyStatus: '', puttyExpiry: '',
    puttyDuration: '', puttyFQDN: '', puttyExtensionTill: '', puttyExtensionReason: '',
    vncStart: '', vncStatus: '', vncExpiry: '', vncDuration: '', vncFQDN: '',
    vncExtensionTill: '', vncExtensionReason: '', lastUpdate: '', address: '', remarks: '',
  };
};

const validateSingleUser = (user: GenerateUser): Record<string, string> => {
  const errors: Record<string, string> = {};
  const required: Array<[keyof GenerateUser, string]> = [
    ['username', 'User Name'], ['userid', 'User ID'], ['email', 'Email'],
    ['primary_group', 'Linux Group'], ['system', 'System Type'], ['tools', 'Tools'],
  ];
  required.forEach(([key, label]) => {
    if (!String(user[key] || '').trim()) errors[key] = `${label} is required`;
  });
  if (user.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(user.email))) {
    errors.email = 'Invalid email format';
  }
  const systemType = (user.system || '').toLowerCase();
  if ((systemType.includes('vnc') || systemType.includes('both')) && !String(user.vncFQDN || '').trim()) {
    errors.vncFQDN = 'VNC FQDN is required for VNC/Both';
  }
  if ((systemType.includes('putty') || systemType.includes('ssh') || systemType.includes('ws') || systemType.includes('both')) &&
      !String(user.puttyFQDN || '').trim()) {
    errors.puttyFQDN = 'PuTTY FQDN is required for PuTTY/SSH/Both';
  }
  if (user.tools) {
    const invalid = String(user.tools).split(':').some(pair => {
      const [tool, count] = pair.split('=');
      return !tool || !count || Number.isNaN(Number(count));
    });
    if (invalid) errors.tools = 'Tools must be in format tool=number:tool=number';
  }
  return errors;
};

const NEW_GUI_SECTION_TONE: Record<'info' | 'accent' | 'success' | 'warning', string> = {
  info: 'from-info to-info',
  accent: 'from-accent to-accent-hover',
  success: 'from-success to-success',
  warning: 'from-warning to-warning',
};

/** Stable section chrome for new GUI — must stay outside SingleUserTab so inputs are not remounted on each keystroke. */
const SingleUserSection: React.FC<{
  tone: keyof typeof NEW_GUI_SECTION_TONE;
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}> = ({ tone, icon, title, children }) => (
  <div className="bg-surface border border-border-subtle rounded-lg overflow-hidden shadow-sm">
    <div className={`bg-gradient-to-r ${NEW_GUI_SECTION_TONE[tone]} px-4 py-2.5 flex items-center gap-2`}>
      {icon}
      <h2 className="text-sm font-semibold text-white">{title}</h2>
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const SingleUserTab: React.FC<Props> = ({
  selectedLicenseServerId, infraServers, startPolling, resetOperation, updateStatus,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const useNew = useNewGui();
  const inputClass = useNew
    ? "mt-1 w-full border border-border-strong rounded px-3 py-2 bg-surface text-ink-primary text-sm focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent"
    : "mt-1 w-full border rounded px-3 py-2";
  const labelClass = useNew ? "block text-sm font-medium text-ink-primary" : "block text-sm font-medium text-gray-700";
  const errClass = useNew ? "text-xs text-danger mt-1" : "text-xs text-red-600 mt-1";
  const [user, setUser] = useState<GenerateUser>(defaultSingleUser);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [skipMountCheck, setSkipMountCheck] = useState(false);

  const updateField = (key: keyof GenerateUser, value: string) => {
    setUser(prev => {
      if (key === 'system') {
        localStorage.setItem('userGen.system', value);
        if (value === 'vnc') return { ...prev, system: value, puttyFQDN: '' };
        if (value === 'putty') return { ...prev, system: value, vncFQDN: '' };
      }
      return { ...prev, [key]: value };
    });
    setErrors(prev => {
      if (!prev[key as string]) return prev;
      const next = { ...prev };
      delete next[key as string];
      return next;
    });
  };

  const canSubmit = () => {
    const required = [selectedLicenseServerId, user.username, user.userid, user.email, user.primary_group, user.system, user.tools];
    const systemType = String(user.system || '').toLowerCase();
    if (systemType.includes('vnc') || systemType.includes('both')) required.push(user.vncFQDN);
    if (systemType.includes('putty') || systemType.includes('ssh') || systemType.includes('ws') || systemType.includes('both')) required.push(user.puttyFQDN);
    return required.every(v => String(v || '').trim().length > 0);
  };

  const handleGenerate = async () => {
    if (!selectedLicenseServerId) {
      updateStatus('Please select a target license server', 'red');
      return;
    }
    const userWithDefaults = { ...user, automation: 'planned', initialPassword: user.initialPassword || 'vlsiexpert@12345' };
    setUser(userWithDefaults);
    const validationErrors = validateSingleUser(userWithDefaults);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      updateStatus('Please fix validation errors before submitting.', 'red');
      return;
    }
    try {
      resetOperation();
      updateStatus('Starting single user generation...', 'orange');
      dispatch(setStatus(translations.en.genOprnWIP));

      const dupCheck = await validateUsersForDuplicates([{ email: user.email, userid: user.userid }]);
      if (dupCheck.hasDuplicates) {
        updateStatus('Duplicate user detected. Please fix and retry.', 'red');
        return;
      }

      const response = await generateInfraUsersLocal({
        selectedLicenseServerId,
        abortOnFailure: false,
        users: [{ ...userWithDefaults, system_type: userWithDefaults.system }] as any[],
        skipMountCheck,
      });

      if (response.data?.operationId) {
        updateStatus('Operation started, monitoring progress...', 'orange');
        startPolling(response.data.operationId);
      } else {
        updateStatus('Single user generation completed.', 'blue');
        dispatch(setStatus(translations.en.genOprnCompleted));
      }
    } catch (error) {
      updateStatus(`Single user generation failed: ${(error as Error).message}`, 'red');
      dispatch(setStatus('Operation Failed'));
    }
  };

  const systemType = String(user.system || '').toLowerCase();

  if (useNew) {
    return (
      <div className="mb-6 space-y-4 font-plex">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SingleUserSection tone="info" icon={<FiUser className="text-white text-sm" />} title="Identity">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>User Name *</label>
                <input className={inputClass} value={user.username || ''} onChange={(e) => updateField('username', e.target.value)} />
                {errors.username && <p className={errClass}>{errors.username}</p>}
              </div>
              <div>
                <label className={labelClass}>Email *</label>
                <input className={inputClass} value={user.email || ''} onChange={(e) => updateField('email', e.target.value)} />
                {errors.email && <p className={errClass}>{errors.email}</p>}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              <div>
                <label className={labelClass}>User ID *</label>
                <input className={inputClass} value={user.userid || ''} onChange={(e) => updateField('userid', e.target.value)} />
                {errors.userid && <p className={errClass}>{errors.userid}</p>}
              </div>
              <div>
                <label className={labelClass}>Mobile</label>
                <input className={inputClass} value={user.mobileno || ''} onChange={(e) => updateField('mobileno', e.target.value)} />
              </div>
            </div>
          </SingleUserSection>

          <SingleUserSection tone="accent" icon={<FiKey className="text-white text-sm" />} title="Account">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Linux Group *</label>
                <input className={inputClass} value={user.primary_group || ''} onChange={(e) => updateField('primary_group', e.target.value)} />
                {errors.primary_group && <p className={errClass}>{errors.primary_group}</p>}
              </div>
              <div>
                <label className={labelClass}>Secondary Groups</label>
                <input type="text" className={inputClass} placeholder="group1 group2" value={user.sec_groups || ''} onChange={(e) => updateField('sec_groups', e.target.value)} />
              </div>
              <div>
                <label className={labelClass}>User Type</label>
                <input className={inputClass} value={user.usertype || ''} onChange={(e) => updateField('usertype', e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              <div>
                <label className={labelClass}>Initial Password</label>
                <div className="relative">
                  <input type={showPassword ? 'text' : 'password'} className={`${inputClass} pr-16`} value={user.initialPassword || ''} onChange={(e) => updateField('initialPassword', e.target.value)} />
                  <button type="button" onClick={() => setShowPassword(p => !p)} className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-accent font-medium">
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>
              <div>
                <label className={labelClass}>Remarks</label>
                <input className={inputClass} value={user.remarks || ''} onChange={(e) => updateField('remarks', e.target.value)} />
              </div>
            </div>
          </SingleUserSection>

          <SingleUserSection tone="success" icon={<FiServer className="text-white text-sm" />} title="System & Access">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>System Type *</label>
                <select className={inputClass} value={user.system || 'vnc'} onChange={(e) => updateField('system', e.target.value)}>
                  <option value="vnc">vnc</option>
                  <option value="putty">putty</option>
                  <option value="both">both</option>
                </select>
                {errors.system && <p className={errClass}>{errors.system}</p>}
              </div>
              <div>
                <label className={labelClass}>Tools *</label>
                <input className={inputClass} placeholder="icc2=1:prime=1" value={user.tools || ''} onChange={(e) => updateField('tools', e.target.value)} />
                <p className="text-xs text-ink-secondary mt-1">Format: tool=number:tool=number</p>
                {errors.tools && <p className={errClass}>{errors.tools}</p>}
              </div>
              <div>
                <label className={labelClass}>PuTTY FQDN</label>
                <select
                  className={`${inputClass} ${systemType.includes('vnc') && !systemType.includes('both') ? 'bg-surface-raised text-ink-secondary' : ''}`}
                  value={user.puttyFQDN || ''} onChange={(e) => updateField('puttyFQDN', e.target.value)}
                  disabled={systemType.includes('vnc') && !systemType.includes('both')}
                >
                  <option value="">Select PuTTY FQDN</option>
                  {infraServers.map((s: any) => { const l = s.serverInfo || `${s.hostName || s.serverFqdn || ''}:${s.port || s.servicePort || ''}`; return <option key={s.id || l} value={l}>{l}</option>; })}
                </select>
                <p className="text-xs text-ink-secondary mt-1">Required when System Type is PuTTY or Both.</p>
                {errors.puttyFQDN && <p className={errClass}>{errors.puttyFQDN}</p>}
              </div>
              <div>
                <label className={labelClass}>VNC FQDN</label>
                <select
                  className={`${inputClass} ${systemType.includes('putty') && !systemType.includes('both') ? 'bg-surface-raised text-ink-secondary' : ''}`}
                  value={user.vncFQDN || ''} onChange={(e) => updateField('vncFQDN', e.target.value)}
                  disabled={systemType.includes('putty') && !systemType.includes('both')}
                >
                  <option value="">Select VNC FQDN</option>
                  {infraServers.map((s: any) => { const l = s.serverInfo || `${s.hostName || s.serverFqdn || ''}:${s.port || s.servicePort || ''}`; return <option key={s.id || l} value={l}>{l}</option>; })}
                </select>
                <p className="text-xs text-ink-secondary mt-1">Required when System Type is VNC or Both.</p>
                {errors.vncFQDN && <p className={errClass}>{errors.vncFQDN}</p>}
              </div>
            </div>
          </SingleUserSection>

          <SingleUserSection tone="warning" icon={<FiCalendar className="text-white text-sm" />} title="Dates">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Setup Date</label>
                <input type="date" className={inputClass} value={user.setupDate || ''} onChange={(e) => updateField('setupDate', e.target.value)} />
              </div>
              <div>
                <label className={labelClass}>Expiry Date</label>
                <input type="date" className={inputClass} value={user.expiryDate || ''} onChange={(e) => updateField('expiryDate', e.target.value)} />
              </div>
            </div>
            <div className="mt-3">
              <label className={labelClass}>Duration</label>
              <input className={inputClass} placeholder="90d or 3m" value={user.duration || ''} onChange={(e) => updateField('duration', e.target.value)} />
            </div>
          </SingleUserSection>
        </div>

        <div className={`rounded-lg border p-4 mt-4 shadow-sm ${skipMountCheck ? 'border-danger/40 bg-danger/10 text-danger' : 'border-warning/40 bg-warning/10 text-warning'}`}>
          <h2 className="text-sm font-semibold mb-2">{skipMountCheck ? 'Execution Warning' : 'Execution Options'}</h2>
          <label className="flex items-center gap-3 text-sm select-none">
            <input type="checkbox" checked={skipMountCheck} onChange={(e) => setSkipMountCheck(e.target.checked)} className="h-4 w-4" />
            <span>Skip mount-point validation (/ve/&lt;group&gt;_home)</span>
          </label>
          <p className="text-xs mt-2">
            {skipMountCheck ? (
              <>Mount-path checks are bypassed. Current mount check path preview: <span className="font-medium">{user.primary_group?.trim() ? `/ve/${String(user.primary_group).trim()}_home` : '/ve/<group>_home'}</span></>
            ) : (
              <>When enabled, the system won&apos;t verify the required mount partition exists before creating the user (advanced; default is strict).</>
            )}
          </p>
        </div>

        <div className="flex items-center">
          <button
            onClick={handleGenerate}
            disabled={!canSubmit()}
            className={`px-5 py-2 rounded text-sm font-medium shadow-sm transition-colors ${canSubmit() ? 'bg-accent text-white hover:bg-accent-hover' : 'bg-surface-raised text-ink-secondary border border-border-subtle cursor-not-allowed'}`}
          >
            Generate User
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Identity */}
        <div className="bg-sky-50 border border-sky-200 rounded-lg p-4 shadow-sm">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">Identity</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">User Name *</label>
              <input className="mt-1 w-full border rounded px-3 py-2" value={user.username || ''} onChange={(e) => updateField('username', e.target.value)} />
              {errors.username && <p className="text-xs text-red-600 mt-1">{errors.username}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Email *</label>
              <input className="mt-1 w-full border rounded px-3 py-2" value={user.email || ''} onChange={(e) => updateField('email', e.target.value)} />
              {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email}</p>}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">User ID *</label>
              <input className="mt-1 w-full border rounded px-3 py-2" value={user.userid || ''} onChange={(e) => updateField('userid', e.target.value)} />
              {errors.userid && <p className="text-xs text-red-600 mt-1">{errors.userid}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Mobile</label>
              <input className="mt-1 w-full border rounded px-3 py-2" value={user.mobileno || ''} onChange={(e) => updateField('mobileno', e.target.value)} />
            </div>
          </div>
        </div>

        {/* Account */}
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 shadow-sm">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">Account</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Linux Group *</label>
              <input className="mt-1 w-full border rounded px-3 py-2" value={user.primary_group || ''} onChange={(e) => updateField('primary_group', e.target.value)} />
              {errors.primary_group && <p className="text-xs text-red-600 mt-1">{errors.primary_group}</p>}
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Secondary Groups</label>
              <input type="text" className="mt-1 w-full border rounded px-3 py-2" placeholder="group1 group2" value={user.sec_groups || ''} onChange={(e) => updateField('sec_groups', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">User Type</label>
              <input className="mt-1 w-full border rounded px-3 py-2" value={user.usertype || ''} onChange={(e) => updateField('usertype', e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Initial Password</label>
              <div className="relative">
                <input type={showPassword ? 'text' : 'password'} className="mt-1 w-full border rounded px-3 py-2 pr-16" value={user.initialPassword || ''} onChange={(e) => updateField('initialPassword', e.target.value)} />
                <button type="button" onClick={() => setShowPassword(p => !p)} className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-blue-600">
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Remarks</label>
              <input className="mt-1 w-full border rounded px-3 py-2" value={user.remarks || ''} onChange={(e) => updateField('remarks', e.target.value)} />
            </div>
          </div>
        </div>

        {/* System & Access */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 shadow-sm">
            <h2 className="text-sm font-semibold text-gray-700 mb-3">System & Access</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">System Type *</label>
                <select className="mt-1 w-full border rounded px-3 py-2" value={user.system || 'vnc'} onChange={(e) => updateField('system', e.target.value)}>
                  <option value="vnc">vnc</option>
                  <option value="putty">putty</option>
                  <option value="both">both</option>
                </select>
                {errors.system && <p className="text-xs text-red-600 mt-1">{errors.system}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Tools *</label>
                <input className="mt-1 w-full border rounded px-3 py-2" placeholder="icc2=1:prime=1" value={user.tools || ''} onChange={(e) => updateField('tools', e.target.value)} />
                <p className="text-xs text-gray-500 mt-1">Format: tool=number:tool=number</p>
                {errors.tools && <p className="text-xs text-red-600 mt-1">{errors.tools}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">PuTTY FQDN</label>
                <select
                  className={`mt-1 w-full border rounded px-3 py-2 ${systemType.includes('vnc') && !systemType.includes('both') ? 'bg-gray-100 text-gray-500' : ''}`}
                  value={user.puttyFQDN || ''} onChange={(e) => updateField('puttyFQDN', e.target.value)}
                  disabled={systemType.includes('vnc') && !systemType.includes('both')}
                >
                  <option value="">Select PuTTY FQDN</option>
                  {infraServers.map((s: any) => { const l = s.serverInfo || `${s.hostName || s.serverFqdn || ''}:${s.port || s.servicePort || ''}`; return <option key={s.id || l} value={l}>{l}</option>; })}
                </select>
                <p className="text-xs text-gray-500 mt-1">Required when System Type is PuTTY or Both.</p>
                {errors.puttyFQDN && <p className="text-xs text-red-600 mt-1">{errors.puttyFQDN}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">VNC FQDN</label>
                <select
                  className={`mt-1 w-full border rounded px-3 py-2 ${systemType.includes('putty') && !systemType.includes('both') ? 'bg-gray-100 text-gray-500' : ''}`}
                  value={user.vncFQDN || ''} onChange={(e) => updateField('vncFQDN', e.target.value)}
                  disabled={systemType.includes('putty') && !systemType.includes('both')}
                >
                  <option value="">Select VNC FQDN</option>
                  {infraServers.map((s: any) => { const l = s.serverInfo || `${s.hostName || s.serverFqdn || ''}:${s.port || s.servicePort || ''}`; return <option key={s.id || l} value={l}>{l}</option>; })}
                </select>
                <p className="text-xs text-gray-500 mt-1">Required when System Type is VNC or Both.</p>
                {errors.vncFQDN && <p className="text-xs text-red-600 mt-1">{errors.vncFQDN}</p>}
              </div>
            </div>
          </div>

          {/* Dates */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 shadow-sm">
            <h2 className="text-sm font-semibold text-gray-700 mb-3">Dates</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">Setup Date</label>
                <input type="date" className="mt-1 w-full border rounded px-3 py-2" value={user.setupDate || ''} onChange={(e) => updateField('setupDate', e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Expiry Date</label>
                <input type="date" className="mt-1 w-full border rounded px-3 py-2" value={user.expiryDate || ''} onChange={(e) => updateField('expiryDate', e.target.value)} />
              </div>
            </div>
            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-700">Duration</label>
              <input className="mt-1 w-full border rounded px-3 py-2" placeholder="90d or 3m" value={user.duration || ''} onChange={(e) => updateField('duration', e.target.value)} />
            </div>
          </div>
        </div>
      </div>

      {/* Execution Options */}
      <div className={`rounded-lg border p-4 mt-4 shadow-sm ${skipMountCheck ? 'border-red-200 bg-red-50 text-red-800' : 'border-amber-200 bg-amber-50 text-amber-900'}`}>
        <h2 className="text-sm font-semibold mb-2">{skipMountCheck ? '⚠️ Execution Warning' : 'Execution Options'}</h2>
        <label className="flex items-center gap-3 text-sm select-none">
          <input type="checkbox" checked={skipMountCheck} onChange={(e) => setSkipMountCheck(e.target.checked)} className="h-4 w-4" />
          <span>Skip mount-point validation (/ve/&lt;group&gt;_home)</span>
        </label>
        <p className="text-xs mt-2">
          {skipMountCheck ? (
            <>Mount-path checks are bypassed. Current mount check path preview: <span className="font-medium">{user.primary_group?.trim() ? `/ve/${String(user.primary_group).trim()}_home` : '/ve/<group>_home'}</span></>
          ) : (
            <>When enabled, the system won&apos;t verify the required mount partition exists before creating the user (advanced; default is strict).</>
          )}
        </p>
      </div>

      <div className="flex items-center">
        <button
          onClick={handleGenerate}
          disabled={!canSubmit()}
          className={`px-4 py-2 rounded-lg ${canSubmit() ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
        >
          Generate User
        </button>
      </div>
    </div>
  );
};

export default SingleUserTab;
